import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { useToast } from "../hooks/use-toast";

const formSchema = z.object({
  name: z.string().min(2, { message: "Моля, въведете вашето име" }),
  email: z.string().email({ message: "Моля, въведете валиден имейл адрес" }),
  phone: z.string().min(6, { message: "Моля, въведете валиден телефонен номер" }),
  service: z.string().optional(),
  message: z.string().min(10, { message: "Моля, въведете вашето съобщение (поне 10 символа)" }),
  privacy: z.boolean().refine((val) => val === true, {
    message: "Трябва да се съгласите с политиката за поверителност",
  }),
});

type FormValues = z.infer<typeof formSchema>;

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      service: "",
      message: "",
      privacy: false,
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // Send form data to the backend API
      const response = await fetch('https://www.nexuspoint.bg/api/vm/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': 'https://www.vmbuild.bg',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      toast({
        title: "Успешно изпратено!",
        description: "Благодарим ви! Вашето запитване беше изпратено успешно. Ще се свържем с вас скоро.",
        variant: "default",
      });
      
      form.reset();
      setFormSubmitted(true);
      setIsSubmitting(false);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Грешка!",
        description: "Възникна проблем при изпращането на формата. Моля, опитайте отново по-късно.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  // Return success message if form was submitted
  if (formSubmitted) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 shadow-md text-center">
        <div className="text-5xl text-green-500 mb-4">✓</div>
        <h3 className="text-2xl font-heading font-bold text-primary mb-4">Благодарим ви!</h3>
        <p className="text-gray-700 mb-6">
          Вашето запитване беше изпратено успешно. Нашият екип ще се свърже с вас скоро.
        </p>
        <Button
          type="button"
          onClick={() => setFormSubmitted(false)}
          className="bg-primary hover:bg-opacity-90 text-white font-bold py-3 px-6 rounded-lg transition duration-300"
        >
          Изпратете ново запитване
        </Button>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="bg-gray-100 rounded-lg p-8 shadow-md">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel className="block text-gray-700 font-bold mb-2">
                Име<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:border-primary"
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage className="text-red-500 text-sm mt-1" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel className="block text-gray-700 font-bold mb-2">
                Имейл<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:border-primary"
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage className="text-red-500 text-sm mt-1" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel className="block text-gray-700 font-bold mb-2">
                Телефон<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="tel"
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:border-primary"
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage className="text-red-500 text-sm mt-1" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel className="block text-gray-700 font-bold mb-2">
                Интересувам се от
              </FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={isSubmitting}
              >
                <FormControl>
                  <SelectTrigger className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:border-primary">
                    <SelectValue placeholder="Изберете услуга" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">Изберете услуга</SelectItem>
                  <SelectItem value="apartment">Ремонт на апартамент</SelectItem>
                  <SelectItem value="bathroom">Ремонт на баня</SelectItem>
                  <SelectItem value="kitchen">Ремонт на кухня</SelectItem>
                  <SelectItem value="painting">Боядисване</SelectItem>
                  <SelectItem value="flooring">Подови настилки</SelectItem>
                  <SelectItem value="terrace">Ремонт на тераса</SelectItem>
                  <SelectItem value="electrical">Електроинсталации</SelectItem>
                  <SelectItem value="plumbing">ВиК услуги</SelectItem>
                  <SelectItem value="other">Друго</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel className="block text-gray-700 font-bold mb-2">
                Съобщение<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={5}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:border-primary"
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage className="text-red-500 text-sm mt-1" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="privacy"
          render={({ field }) => (
            <FormItem className="mb-6 flex flex-row items-start space-x-2">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting}
                  className="mt-1"
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm text-gray-700">
                  Съгласен съм с <a href="#" className="text-primary hover:underline">политиката за поверителност</a> и обработката на личните ми данни<span className="text-red-500">*</span>
                </FormLabel>
                <FormMessage className="text-red-500 text-sm mt-1" />
              </div>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full bg-[#D95204] hover:bg-opacity-90 text-white font-bold py-3 px-6 rounded-lg transition duration-300"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Изпращане..." : "Изпратете запитване"}
        </Button>
      </form>
    </Form>
  );
};

export default ContactForm;
